<template>
  <div class="ai-page">
    <!-- ============ 品牌头部 ============ -->
    <header class="sub-header">
      <div class="sub-header-inner">
        <button type="button" class="back-btn" aria-label="返回" @click="goBack">
          <van-icon name="arrow-left" />
        </button>
        <h1 class="sub-title">我的审核记录</h1>
        <span class="sub-right">共 {{ records.length }} 份</span>
      </div>
    </header>

    <main class="page-content">
      <!-- 导语 -->
      <p class="page-lead">历史审核结果已归档，点击文件可展开查看审核步骤与整改建议。</p>

      <!-- 审核记录 -->
      <section class="audit-section">
        <ul class="audit-list">
          <li v-for="r in records" :key="r.id" class="rec-item" :class="{ open: openId === r.id }">
            <div class="rec-head" @click="toggle(r.id)">
              <van-icon name="notes-o" class="f-icon" />
              <div class="rec-main">
                <h3>{{ r.name }}</h3>
                <p class="rec-time">{{ r.time }}</p>
              </div>
              <span class="risk-tag" :class="'lv-' + r.verdict">{{ r.riskText }}</span>
              <van-icon name="arrow-down" class="chev" :class="{ rot: openId === r.id }" />
            </div>

            <transition name="acc">
              <div v-if="openId === r.id" class="audit-detail">
                <p class="steps-title">审核步骤 · 共 {{ r.steps.length }} 步</p>
                <ol class="steps">
                  <li v-for="s in r.steps" :key="s.no" class="step">
                    <div class="step-head">
                      <span class="step-no">{{ s.no }}</span>
                      <h4 class="step-name">{{ s.title }}</h4>
                      <span class="verdict-tag" :class="'lv-' + s.verdict">{{ verdictText(s.verdict) }}</span>
                    </div>
                    <div class="kv">
                      <span class="k">检查要点说明</span>
                      <p class="v">{{ s.keypoints }}</p>
                    </div>
                    <div class="kv">
                      <span class="k">检查结果</span>
                      <p class="v">{{ s.result }}</p>
                    </div>
                    <div class="kv" v-if="s.suggestion">
                      <span class="k">整改建议</span>
                      <p class="v">{{ s.suggestion }}</p>
                    </div>
                  </li>
                </ol>
              </div>
            </transition>
          </li>
        </ul>
      </section>
    </main>
  </div>
</template>

<script>
import guoqiShare from '../utils/guoqiShare'
import pageAccess from '../utils/pageAccess'
export default {
  name: 'guoqiAi',
  mixins: [guoqiShare, pageAccess],
  data () {
    return {
      // 页面访问日志：页面名称
      pageName: '我的审核记录',
      // 微信分享：链接为当前路由
      shareTitle: 'AI 合同审核：智能识别合同条款风险',
      shareDesc: '黑名单检查、涉假主体比对、关联企业穿透、条款风险识别，逐条给出审核结论',
      openId: null, // 单开手风琴：当前展开的记录 id
      records: [
        {
          id: 'r1',
          name: '煤炭购销框架合同.docx',
          time: '2026-09-02 14:20',
          verdict: 'warn',
          riskText: '中风险',
          steps: [
            { no: '第一步', title: '黑名单检查', keypoints: '比对合同相对方及其关联方是否命中涉假企业与黑名单库。', result: '未命中黑名单及涉假企业名录。', suggestion: '', verdict: 'pass' },
            { no: '第二步', title: '涉假主体比对', keypoints: '比对企业名称、统一社会信用代码与国资委央企名录、假央企名单。', result: '合同主体名称与央企名录存在近似，需核验授权。', suggestion: '要求提供与央企的授权或股权文件，并线下核验原件。', verdict: 'warn' },
            { no: '第三步', title: '关联企业穿透', keypoints: '穿透股东、实际控制人是否存在异常关联或高风险主体。', result: '两层股权穿透未发现异常关联。', suggestion: '', verdict: 'pass' },
            { no: '第四步', title: '条款风险识别', keypoints: '识别付款、交货、违约责任与管辖条款风险。', result: '预付款比例偏高、违约金上限过低。', suggestion: '下调预付款比例并补充担保与违约责任条款。', verdict: 'warn' }
          ]
        },
        {
          id: 'r2',
          name: '供应链金融合作协议.pdf',
          time: '2026-09-02 11:05',
          verdict: 'fail',
          riskText: '高风险',
          steps: [
            { no: '第一步', title: '黑名单检查', keypoints: '比对交易对手是否命中涉假主体与黑名单库。', result: '交易对手命中黑名单，风险等级高。', suggestion: '立即终止合作洽谈并升级至法务复核。', verdict: 'fail' },
            { no: '第二步', title: '涉假主体比对', keypoints: '比对企业名称、统一社会信用代码与国资委央企名录。', result: '名义挂靠央企背景，实际并非央企授权主体。', suggestion: '核实全套证照原件并补充授权文件。', verdict: 'fail' },
            { no: '第三步', title: '条款风险识别', keypoints: '识别担保、反担保、利率与违约条款。', result: '存在隐形担保与无限追索条款。', suggestion: '重新拟定担保结构并设定责任上限。', verdict: 'warn' }
          ]
        },
        {
          id: 'r3',
          name: '设备采购补充协议.docx',
          time: '2026-09-01 17:40',
          verdict: 'pass',
          riskText: '低风险',
          steps: [
            { no: '第一步', title: '黑名单检查', keypoints: '比对交易对手及其关联方是否命中涉假企业与黑名单库。', result: '未命中黑名单及涉假企业名录。', suggestion: '', verdict: 'pass' },
            { no: '第二步', title: '涉假主体比对', keypoints: '比对企业名称、统一社会信用代码与国资委央企名录。', result: '主体名称与证照信息一致。', suggestion: '', verdict: 'pass' },
            { no: '第三步', title: '关联企业穿透', keypoints: '穿透股东、实际控制人是否存在异常关联。', result: '未发现异常关联主体。', suggestion: '', verdict: 'pass' },
            { no: '第四步', title: '条款风险识别', keypoints: '识别付款、交货、违约责任与管辖条款风险。', result: '付款与违约责任条款清晰。', suggestion: '', verdict: 'pass' },
            { no: '第五步', title: '审核结论', keypoints: '汇总全部步骤，给出整体审核结论。', result: '低风险，可正常签署。', suggestion: '', verdict: 'pass' }
          ]
        }
      ]
    }
  },
  methods: {
    goBack () {
      if (window.history.length > 1) {
        this.$router.back()
      } else {
        this.$router.replace('/guoqi')
      }
    },
    toggle (id) {
      this.openId = this.openId === id ? null : id
    },
    verdictText (v) {
      const map = { pass: '通过', warn: '需关注', fail: '需整改' }
      return map[v] || '通过'
    }
  }
}
</script>

<style lang="less" scoped>
@page-bg: #faf7f1;
@surface: #fffdfc;
@ink: #142033;
@body: #333a45;
@muted: #7f858d;
@brand: #ef4f16;
@brand-light: #f47a3c;
@brand-soft: #fff1e8;
@gold: #d7a34a;
@line: #e9e6e1;
@green: #0f9d5c;
@green-bg: #e3f7ec;
@warn: #d97706;
@warn-bg: #fcf0dc;
@red: #e11d48;
@red-bg: #fce6e9;

button { margin: 0; padding: 0; border: 0; color: inherit; font: inherit; background: transparent; appearance: none; }
ol, ul { margin: 0; padding: 0; list-style: none; }

.ai-page { min-height: 100vh; box-sizing: border-box; color: @ink; background: @page-bg; font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", sans-serif; }

/* ================= 品牌头部 ================= */
.sub-header { position: sticky; top: 0; z-index: 20; background: rgba(250, 247, 241, 0.94); backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px); border-bottom: 1px solid @line; }
.sub-header-inner { position: relative; display: flex; align-items: center; max-width: 750px; height: 88px; margin: 0 auto; padding: 0 24px; box-sizing: border-box; }
.back-btn { display: inline-flex; align-items: center; justify-content: center; width: 60px; height: 60px; color: @ink; cursor: pointer; }
.back-btn .van-icon { font-size: 38px; }
.sub-title { position: absolute; right: 24px; left: 24px; margin: 0; color: @ink; font-family: "Songti SC", "STSong", "SimSun", serif; font-size: 34px; font-weight: 700; letter-spacing: 1px; line-height: 88px; text-align: center; pointer-events: none; }
.sub-right { position: absolute; right: 24px; color: @muted; font-size: 21px; }

/* ================= 页面主体 ================= */
.page-content { max-width: 750px; margin: 0 auto; padding: 0 0 56px; }
.page-lead { margin: 0; padding: 30px 40px 0; color: @body; font-size: 24px; line-height: 1.7; }

/* ================= 审核记录 ================= */
.audit-section { margin-top: 26px; padding: 0 40px; background: @surface; border-top: 1px solid @line; border-bottom: 1px solid @line; }
.rec-item { position: relative; }
.rec-item::after { position: absolute; right: 0; bottom: 0; left: 0; height: 1px; background: @line; content: ""; }
.rec-item:last-child::after { display: none; }

.rec-head { display: flex; align-items: center; min-height: 128px; padding: 20px 0; cursor: pointer; }
.f-icon { flex: 0 0 auto; color: @brand; font-size: 38px; }
.rec-main { flex: 1; min-width: 0; margin-left: 20px; }
.rec-main h3 { margin: 0; color: @ink; font-size: 28px; font-weight: 700; line-height: 1.4; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
.rec-time { margin: 8px 0 0; color: @muted; font-size: 20px; }
.chev { flex: 0 0 auto; margin-left: 12px; color: #a9aeb5; font-size: 26px; transition: transform 0.18s ease; }
.chev.rot { transform: rotate(180deg); }

.risk-tag { display: inline-block; flex: 0 0 auto; margin-left: 16px; padding: 6px 16px; border-radius: 999px; font-size: 20px; font-weight: 600; white-space: nowrap; }
.risk-tag.lv-pass { color: @green; background: @green-bg; }
.risk-tag.lv-warn { color: @warn; background: @warn-bg; }
.risk-tag.lv-fail { color: @red; background: @red-bg; }

/* 展开区 */
.audit-detail { padding: 2px 0 30px 58px; }
.steps-title { margin: 0 0 6px; color: @muted; font-size: 21px; }
.step { padding: 26px 0; border-top: 1px dashed @line; }
.step-head { display: flex; align-items: center; }
.step-no { flex: 0 0 auto; color: @brand; font-family: "Songti SC", "STSong", "SimSun", serif; font-size: 28px; font-weight: 700; }
.step-name { flex: 1; min-width: 0; margin: 0 0 0 14px; color: @ink; font-size: 27px; font-weight: 700; line-height: 1.4; }
.verdict-tag { flex: 0 0 auto; margin-left: 12px; padding: 4px 14px; border-radius: 999px; font-size: 20px; font-weight: 600; }
.verdict-tag.lv-pass { color: @green; background: @green-bg; }
.verdict-tag.lv-warn { color: @warn; background: @warn-bg; }
.verdict-tag.lv-fail { color: @red; background: @red-bg; }

.kv { margin-top: 18px; }
.kv .k { display: block; color: @muted; font-size: 21px; }
.kv .v { margin: 8px 0 0; color: @body; font-size: 23px; line-height: 1.7; }

/* 手风琴展开动画 */
.acc-enter-active { animation: acc-in 0.18s ease; }
@keyframes acc-in {
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
}

/* 底部提示 */
.page-hint { margin: 0; padding: 34px 40px 0; color: @muted; font-size: 21px; line-height: 1.6; text-align: center; }

@media (max-width: 420px) {
  .sub-header-inner { height: 78px; padding: 0 14px; }
  .sub-title { right: 14px; left: 14px; font-size: 30px; line-height: 78px; }
  .sub-right { right: 14px; font-size: 17px; }
  .page-lead { padding: 24px 22px 0; font-size: 21px; }
  .audit-section { padding: 0 22px; }
  .rec-main h3 { font-size: 25px; }
  .risk-tag { font-size: 18px; padding: 4px 12px; }
  .audit-detail { padding-left: 44px; }
  .step-name { font-size: 24px; }
  .kv .v { font-size: 21px; }
  .page-hint { padding-right: 22px; padding-left: 22px; font-size: 18px; }
}
</style>
